export const printSectors = [
  {
    title: 'Flexographic Packaging',
    icon: 'Package',
    description: 'Complete MIS solutions tailored for flexographic packaging operations, from estimating complex multi-colour jobs to managing substrate inventories and production scheduling.',
  },
  {
    title: 'Digital Printing',
    icon: 'Printer',
    description: 'Purpose-built workflows for digital print environments, handling variable data, web-to-print integration, and the unique costing models that digital demands.',
  },
  {
    title: 'Folding Cartons',
    icon: 'Box',
    description: 'Specialised systems for folding carton manufacturers, covering dieline management, multi-process estimating, and integration with CAD and prepress tools.',
  },
  {
    title: 'Label Printing',
    icon: 'Tag',
    description: 'Dedicated label printing workflows supporting combination printing, die management, material optimisation, and compliance tracking for regulated industries.',
  },
  {
    title: 'Mailing',
    icon: 'Mail',
    description: 'Comprehensive solutions for mailing houses, covering data processing, postal optimisation, machine scheduling, and Royal Mail compliance reporting.',
  },
  {
    title: 'Commercial Printing',
    icon: 'Layers',
    description: 'Proven MIS for litho and commercial printers — multi-section estimating, sheet and ganging optimisation, press scheduling and full job costing from prepress right through to despatch.',
  },
  {
    title: 'Web-To-Print',
    icon: 'Globe',
    description: 'Seamless integration between your online storefront and production workflow. Orders flow directly from your web portal into Imprint for instant processing.',
  },
  {
    title: 'Wide Format',
    icon: 'Maximize',
    description: 'Solutions designed for wide-format and large-format printing, managing substrate variations, installation scheduling, and site survey documentation.',
  },
  {
    title: 'Print Management',
    icon: 'ClipboardList',
    description: 'End-to-end tools for print management companies, coordinating outsourced production, managing supplier relationships, and maintaining margin visibility.',
  },
];

export const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Print Sectors', href: '/print-sectors', isPage: true },
  { label: 'About Us', href: '/about', isPage: true },
  { label: 'Meet the Team', href: '/team', isPage: true },
  { label: 'Integrations', href: '/integrations', isPage: true },
  {
    label: 'Solutions',
    href: '#solutions',
    children: [
      { label: 'Packaging MIS Software', href: '/solutions/packaging-mis-software', isPage: true },
      { label: 'Label Printing MIS', href: '/solutions/label-mis-software', isPage: true },
      { label: 'Carton & Folding Carton MIS', href: '/solutions/carton-mis-software', isPage: true },
      { label: 'Print Estimating Software', href: '/solutions/print-estimating-software', isPage: true },
      { label: 'All Solutions', href: '#solutions' },
    ],
  },
  { label: 'Resources', href: '#resources' },
  { label: 'Contact', href: '#contact' },
];

export const heroRotatingWords = ['intelligent', 'smarter', 'cutting-edge'];

export const imagineFeatures = [
  {
    title: 'Quick & Accurate Estimating',
    summary: 'The ability to quickly generate estimates for a variety of applications and then feed them into a system that can monitor and report your workflow right through to fulfilment.',
    details: 'Our estimating engine supports complex multi-process jobs, automatically calculating material costs, labour, overheads, and margin. Generate professional quotes in seconds, not hours, and seamlessly convert them to live orders with a single click.',
  },
  {
    title: 'Eliminate Bottlenecks',
    summary: 'A system created to identify and relieve the pressure points and bottle necks within your business.',
    details: 'Real-time production dashboards highlight exactly where delays occur. Our intelligent scheduling engine re-routes work automatically, while alerts notify managers the moment a job falls behind. The result: smoother throughput and happier customers.',
  },
  {
    title: 'Scalable & Adaptive Solutions',
    summary: 'Solutions that can continue to develop and adapt with your company.',
    details: 'Imprint is built on a modular architecture that grows with you. Start with core estimating and order processing, then add stock control, warehousing, CRM, or machine monitoring when ready. API integrations connect Imprint to your existing tools seamlessly.',
  },
  {
    title: 'Over 40 Years of Expertise',
    summary: 'A company with over 40 years of providing solutions to the industry. Their team consisting entirely of people with a background in the industry and IT.',
    details: 'Founded in the heart of the UK print sector, Imprint combines deep industry knowledge with cutting-edge software engineering. Every member of our team has hands-on printing or packaging experience, meaning we speak your language and understand your challenges.',
  },
  {
    title: 'Real Business Savings',
    summary: 'A partner to help you implement real savings to your business.',
    details: 'Customers typically see a 15-25% reduction in administrative overhead within the first year. Automated workflows, accurate costing, and waste reduction tools deliver measurable ROI that goes straight to your bottom line.',
  },
];

export const journeyContent = {
  subtitle: 'Your Journey',
  title: 'It starts with you...',
  paragraphs: [
    'Recently the print and packaging landscape has radically changed. You need a software partner that can adapt and change with you. The best way for us to build the right system is to get to know you and your business and the efficiencies you are trying to achieve.',
    'We see our customers as a business partner; moving forward together to continue to develop solutions that help automate the workflow process.',
    "We don't see how a 'one size fits all' can truly be effective at delivering you the results you need. Imprint doesn't sell packages we provide you with the system you need.",
  ],
};

export const solutions = [
  {
    title: 'Imprint Desktop',
    description: 'Your central command centre. A personalised dashboard that gives every user instant visibility of their tasks, alerts, and KPIs. Customisable widgets display real-time data so you always know the pulse of your business.',
    icon: 'Monitor',
  },
  {
    title: 'Data Reporting',
    description: 'Transform raw data into actionable intelligence. Build custom reports with drag-and-drop simplicity, schedule automated email delivery, and drill down into any metric. Make confident, data-driven decisions every day.',
    icon: 'BarChart3',
  },
  {
    title: 'Estimating',
    description: 'Generate accurate, competitive quotes in seconds. Our powerful estimating engine handles everything from simple single-colour jobs to complex multi-process packaging, automatically factoring materials, labour, machine time, and overheads.',
    icon: 'Calculator',
  },
  {
    title: 'Order Processing',
    description: 'Streamline your entire order lifecycle from receipt to delivery. Automated job bags, intelligent scheduling, and real-time status tracking ensure nothing falls through the cracks while keeping your customers informed at every stage.',
    icon: 'ClipboardList',
  },
  {
    title: 'Production Control',
    description: 'Take command of your production floor. Visual scheduling boards, capacity planning tools, and real-time job tracking give you complete control over every press, finishing line, and dispatch operation in your facility.',
    icon: 'Settings',
  },
  {
    title: 'SFDC & Machine Monitoring',
    description: 'Capture shop floor data automatically. Connect directly to your presses and finishing equipment to record run speeds, waste, downtime, and operator performance. Compare actual against estimated to continuously improve profitability.',
    icon: 'Activity',
  },
  {
    title: 'Stock Control',
    description: 'Never run out of critical materials again. Real-time stock levels, automatic re-order points, batch tracking, and supplier management ensure your materials are always available when you need them, without tying up excess capital.',
    icon: 'Package',
  },
  {
    title: 'Warehousing',
    description: 'Manage finished goods with precision. Location-based storage, pick-and-pack workflows, despatch management, and full integration with courier systems ensure orders reach your customers quickly and accurately.',
    icon: 'Warehouse',
  },
];

export const clientLogos = [
  { name: 'PMG Print Management', initials: 'PMG' },
  { name: 'Simple Cartons', initials: 'SC' },
  { name: 'Wetherby', initials: 'WB' },
  { name: 'Mail Solutions', initials: 'MS' },
  { name: 'A1 Security Print', initials: 'A1' },
  { name: 'Elanders', initials: 'EL' },
  { name: 'Glossop Cartons', initials: 'GC' },
  { name: 'Hobbs The Printers', initials: 'HB' },
  { name: 'Latcham', initials: 'LA' },
];

export const testimonials = [
  {
    quote: "Overall I'm very happy with Imprint. I've used several MIS systems in the past and Imprint is far and away the best. You can tell that the system has been designed by printers because they understand our particular challenges. The information that Imprint provides has enabled me to make better informed commercial decisions than ever before.",
    company: 'A1 Security Print',
  },
  {
    quote: "Our new Imprint-MIS system will change the way we do business, it is being tailored to meet the exact needs of PMG and our processes. The Imprint team has used a deeper understanding of our day-to-day business needs to create a system so intelligent and flexible that we will be able to work at a different level.",
    company: 'PMG Print Management',
  },
  {
    quote: "We're delighted with Imprint and really pleased that the team has been able to design a bespoke package that takes into account all the requirements of our carton-based business, rather than just offering a generic 'one size fits all' solution. The system has been built from the ground up especially for our and many of our colleagues in the industry's needs.",
    company: 'Glossop Cartons',
  },
  {
    quote: "With the Imprint team's patience and guidance we have created a flexible and stable system first time which looks to enable us to improve our processes and procedures throughout the company. Additionally, the versatility of MyData Reporting is Revolutionary. The best thing since sliced bread!",
    company: 'Hobbs The Printers',
  },
  {
    quote: "Following a detailed review of generic, industry specific and in-house written software, Document Despatch chose to install the Imprint MIS to manage virtually all its operational systems.",
    company: 'Document Despatch',
  },
];

export const resources = [
  {
    title: 'Simply Cartons & Imprint-MIS',
    slug: 'simply-cartons-imprint-mis',
    category: 'Installation',
    date: '29/05/2025',
    excerpt: 'Discover how Simply Cartons transformed their carton manufacturing workflow with a tailored Imprint MIS installation, achieving dramatic efficiency gains across estimating and production.',
    body: 'Simply Cartons, a leading UK carton manufacturer, faced growing challenges managing their increasingly complex production workflows. Manual processes were causing delays in estimating, order processing, and production scheduling.\n\nAfter partnering with Imprint, a bespoke MIS solution was configured to match their exact operational needs. The system was tailored to handle the unique requirements of carton manufacturing — from multi-component estimating with complex dielines to automated production scheduling across folding, gluing, and finishing lines.\n\nWithin the first six months, Simply Cartons saw a 30% reduction in estimating time and a significant improvement in on-time delivery rates. The real-time production dashboards gave management instant visibility of bottlenecks, enabling proactive decision-making.\n\n"Imprint understood our business from day one," said the Operations Director at Simply Cartons. "The system feels like it was built specifically for us — because it was."',
    image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=600&h=400&fit=crop',
  },
  {
    title: "GCP's Machine Monitoring Project",
    slug: 'gcp-machine-monitoring-project',
    category: 'Installation',
    date: '29/05/2025',
    excerpt: 'GCP partnered with Imprint to implement a comprehensive machine monitoring system, providing real-time visibility into press performance and operator efficiency across their entire facility.',
    body: 'GCP, one of the UK\'s most established print groups, recognised the need for better visibility into their shop floor operations. With multiple presses running across shifts, understanding true machine utilisation and operator performance was critical to improving profitability.\n\nImprint\'s SFDC & Machine Monitoring module was deployed across GCP\'s entire press hall, connecting directly to each machine\'s control systems. The implementation captured real-time data on run speeds, waste percentages, makeready times, and downtime reasons — all automatically, without requiring operator input.\n\nThe results were transformative. Within three months, GCP identified that actual run speeds were 15% below optimal on several key presses. Armed with this data, they implemented targeted training and maintenance programmes that recovered over £200,000 in annual production capacity.\n\nThe monitoring dashboards now form the centrepiece of GCP\'s daily production meetings, with colour-coded performance indicators making it easy to spot trends and take action.',
    image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600&h=400&fit=crop',
  },
  {
    title: "Imprint's New Automation Engine Integration Goes Live",
    slug: 'automation-engine-integration',
    category: 'Events',
    date: '29/05/2025',
    excerpt: 'Our latest automation engine integration delivers seamless connectivity between Imprint MIS and leading prepress workflows, reducing manual touchpoints and accelerating time-to-press.',
    body: 'We are excited to announce that our new Automation Engine integration is now live and available to all Imprint customers. This powerful new capability creates a seamless bridge between your Imprint MIS and leading prepress workflow systems.\n\nThe integration eliminates the need for manual re-keying of job specifications into prepress systems. When an order is confirmed in Imprint, job details — including dimensions, colours, substrate, finishing requirements, and file references — are automatically transmitted to your prepress workflow.\n\nKey features of the new integration include:\n\n• Automatic job ticket generation from Imprint order data\n• Bi-directional status updates between MIS and prepress\n• Preflight report integration back into Imprint job records\n• Support for JDF/JMF protocols and REST API connections\n• Compatible with major prepress systems including Esko, Enfocus, and Callas\n\nEarly adopters have reported a 40% reduction in prepress setup time and near-elimination of data entry errors. The integration is available as part of our standard support package — contact your account manager to arrange a demonstration.',
    image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&h=400&fit=crop',
  },
];

export const footerData = {
  address: {
    line1: '27 Fairycroft Road',
    line2: 'Saffron Walden',
    line3: 'CB10 1LZ',
    line4: 'United Kingdom',
  },
  phone: '+44 (0)1245 231670',
  socialLinks: [
    { name: 'Facebook', href: 'https://www.facebook.com/imprintmis/' },
    { name: 'Twitter', href: 'https://twitter.com/ImprintMis' },
    { name: 'LinkedIn', href: 'https://www.linkedin.com/company/imprint-business-systems/' },
    { name: 'YouTube', href: 'https://www.youtube.com/channel/UC9XbL_UDxILoA3pacvYppLw' },
  ],
  copyright: `Copyright \u00A9 ${new Date().getFullYear()} Imprint Business Systems Limited`,
  regNumber: 'All rights reserved | Company Reg. No: 1925981',
};

export const sectorImages = [
  {
    src: '/service-details-bg-1.webp',
    alt: 'Printing Production',
    label: 'Production',
  },
  {
    src: '/computer-system.webp',
    alt: 'Imprint MIS Software',
    label: 'Technology',
  },
  {
    src: '/labels.webp',
    alt: 'Label Solutions',
    label: 'Labels',
  },
  {
    src: '/blog-4-img3.webp',
    alt: 'Print Quality Control',
    label: 'Quality',
  },
];

export const managementTeam = [
  {
    id: 'nick-bourne',
    name: 'Nick Bourne',
    role: 'Managing Director',
    image: '/team/nick-bourne.webp',
    linkedin: null,
    bio: [
      "Nick joined Imprint as a support developer in 1993, following a hands-on year in the industry learning how print works at the coal face. Over the years he worked in various roles, stepping up to Technical Director in 2002 and later moving to Managing Director. Nick has qualifications in both Engineering and Computer Science, an ideal background to an IT company serving an industry that is a form of engineering.",
      "Management duties aside, Nick specialises in integration work between our products and other applications commonly used by our customers. He is an active member of the CIP4 group and regularly attends JDF interoperability meetings, developing and testing our software with our partners from the industry.",
      "Nick's years of expertise and experience in providing our software solutions, ensures that Imprint delivers what it promises.",
      "A Saturday morning will often find Nick attending a parkrun or failing that walking around a muddy field with his Labrador Enzo. He is a keen motorsport fan, both spectating and competing.",
    ],
  },
  {
    id: 'pete-horwood',
    name: 'Pete Horwood',
    role: 'Commercial Director',
    image: '/team/pete-horwood.webp',
    linkedin: 'https://www.linkedin.com/in/peterhorwood/',
    bio: [
      "With over 15 years' experience in the industry Peter feels privileged to have had to opportunities to experience Print in so many of its glorious forms. Starting with supplying hardware solutions to various MIS companies for data collection and machine monitoring projects. This led to a connection and partnership with Imprint-MIS back in 2011 and continuing to grow that relationship until ultimately joining the team in 2019.",
      "Within Imprint he leads our sales team, works closely with customers on various implementation projects and consults closely with our programmers and support team to help ensure Imprint is delivering the very best value, efficiencies and savings for customers.",
      "Pete prides himself on being approachable, pragmatic and dedicated to understanding how a customer's business works, to then Listen and feedback with an Imprint Solution that works.",
      "In his spare time Pete enjoys playing guitar, cooking, camping and making the most of the outdoors (when we get the weather) with his family, friends and Gizmo the dog.",
    ],
  },
  {
    id: 'kevin-charter',
    name: 'Kevin Charter',
    role: 'Company Secretary',
    image: '/team/kevin-charter.webp',
    linkedin: null,
    bio: [
      "Kevin has worked with the company for over 30 years, was a former user of Imprint 10 years before and brings nearly 40 years of experience within the print industry. Throughout his career, he has developed extensive knowledge across all aspects of print production, customer support, and technical implementation.",
      "His role has involved delivering training, overseeing new site installations, and supporting clients through system integration and operational setup. With decades of hands-on industry experience, Kevin is known for his practical expertise, problem-solving approach, and commitment to helping customers achieve the best possible results.",
      "Of late he has taken on financial responsibilities as Company Secretary drawing on prior experience in the finance industry before his involvement in the print trade.",
      "Outside of work Kevin has long had an interest in various musical genres since the age of 8, currently performing in Orchestras, Concert and Brass Bands, plus a Phil Collins tribute band.",
    ],
  },
  {
    id: 'phil-elson',
    name: 'Phil Elson',
    role: 'Training and Implementation Manager',
    image: '/team/phil-elson.webp',
    linkedin: 'https://www.linkedin.com/in/phil-elson-601a2717/',
    bio: [
      "With more than 20 years of experience in commercial and digital print, Phil brought his industry expertise into the MIS sector and has now been part of the Imprint team for 15 years. During that time, he has built extensive knowledge drawn from every stage of his professional career.",
      "As a Project Manager, Phil has overseen countless installations of Imprint's MIS solutions, delivering essential training and ongoing customer support throughout the process. Working as part of the wider support team, he also assists customers through a variety of communication channels, handling everything from straightforward enquiries to more complex, technically focused system issues.",
      "Phil's passion for projects and problem solving is infectious and our customers always appreciate his practical and down to earth approach to providing results.",
      "Outside of work, Phil enjoys spending time at the gym, going for long walks, and heading off on adventures in his pride and joy — Elsie the Motorhome.",
    ],
  },
  {
    id: 'cheryl-ellins',
    name: 'Cheryl Ellins',
    role: 'Customer Service Manager',
    image: '/team/cheryl-ellins.webp',
    linkedin: null,
    bio: [
      "With 43 years of experience in the print industry, Cheryl built her career around customer-focused roles and developing strong, lasting relationships with clients and colleagues alike. Throughout her career, she has always taken great pride in providing and ensuring customers feel valued, listened to, and supported.",
      "Before joining Imprint in June 2010 as part of the customer support team, she had already established a strong working relationship with the company through a previous employer. That connection made the transition feel natural and reinforced her passion for working closely with customers to deliver the best possible service.",
      "Always a customer favourite, Cheryl is known for being approachable, dependable, and committed to helping others. Cheryl continues to find satisfaction in building positive relationships and supporting customers with professionalism and care.",
      "Outside of work, Cheryl enjoys keeping active through gym sessions, running, and walking, which helps her maintain a positive balance between work and personal life. She also enjoys relaxing and socialising with family and friends — often with the occasional glass of wine or gin and tonic thrown in.",
    ],
  },
  {
    id: 'nat-todman',
    name: 'Nat Todman',
    role: 'Product Development Manager',
    image: '/team/nat-todman.webp',
    linkedin: null,
    bio: [
      "With 10+ years of experience programming, Nat joined Imprint in 2017 as a full-stack software developer and has played a key role in the continued development of the company's web application suite. He is heavily involved in a wide range of projects, including Shop Floor Data Capture, Warehousing, Non-Conformance, and Dashboard, helping customers gain greater visibility and control across their operations.",
      "With experience across both front-end and back-end development, Nat works on designing and delivering practical solutions that meet the evolving needs of commercial print businesses. He is particularly interested in creating intuitive user experiences and modernising business applications while ensuring they remain robust and maintainable.",
      "Nat has been involved in the ongoing evolution of Imprint's web technologies and works closely with colleagues and customers to translate business requirements into effective software solutions.",
      "Outside of work, Nat can often be found on the football pitch competing in a veterans' league, usually when not recovering from the latest injury. He also enjoys playing badminton each week and keeping active through sport.",
    ],
  },
];


export const integrationCategories = [
  {
    id: 'automation',
    title: 'Automation & JDF',
    description:
      'Long-term members of the CIP4 organisation with active JDF integrations across major print production platforms.',
    partners: [
      { name: 'Kodak', logo: '/integrations/kodak.png', url: null },
      { name: 'Agfa', logo: '/integrations/agfa.png', url: null },
      { name: 'Heidelberg', logo: '/integrations/heidelberg.jpg', url: null },
      { name: 'MBO', logo: null, url: null },
    ],
  },
  {
    id: 'accounting',
    title: 'Accounting',
    description:
      'Push invoices, purchase orders and journal entries straight from Imprint into your finance system.',
    partners: [
      { name: 'Sage', logo: '/integrations/sage-clean.png', url: null },
      { name: 'Xero', logo: '/integrations/xero.svg', url: null },
      { name: 'QuickBooks', logo: '/integrations/quickbooks.png', url: null },
    ],
  },
  {
    id: 'pre-press',
    title: 'Pre-press',
    description:
      'End-to-end pre-press automation — from job ticket to imposed plate — with the industry\'s leading workflow engines.',
    partners: [
      { name: 'Enfocus Switch', logo: '/integrations/enfocus.png', url: null },
      { name: 'Esko Automation Engine', logo: '/integrations/esko.jpg', url: null },
      { name: 'Esko Phoenix', logo: '/integrations/phoenix.jpg', url: null, showName: true },
      { name: 'Agfa', logo: '/integrations/agfa.png', url: null },
    ],
  },
  {
    id: 'web2print',
    title: 'Web2Print',
    description:
      'Online-order storefronts flow directly into Imprint MIS as fully-costed, production-ready jobs.',
    partners: [
      { name: 'XMPie', logo: '/integrations/xmpie.jpg', url: null },
      { name: 'Infigo', logo: '/integrations/infigo.png', url: null },
      { name: 'Vpress', logo: '/integrations/vpress.png', url: null },
      { name: 'ROI360', logo: '/integrations/roi360.png', url: null },
      { name: 'PrintJob', logo: '/integrations/printjob.png', url: null },
    ],
  },
  {
    id: 'machine-integration',
    title: 'Machine Integration',
    description:
      'Real-time machine data — makeready, run speeds, waste — flowing back into MIS for accurate costing and monitoring.',
    partners: [
      { name: 'Heidelberg', logo: '/integrations/heidelberg.jpg', url: null },
      { name: 'HP', logo: '/integrations/hp.png', url: null },
      { name: 'Koenig & Bauer', logo: '/integrations/koenig-bauer.jpg', url: null },
      { name: 'Canon', logo: '/integrations/canon.png', url: null },
      { name: 'Fuji', logo: '/integrations/fujifilm.png', url: null },
      { name: 'Komori', logo: '/integrations/komori.png', url: null },
      { name: 'Jet Tech Group', logo: '/integrations/jet-tech.png', url: null },
    ],
  },
  {
    id: 'shipping',
    title: 'Shipping',
    description:
      'Automate carrier bookings, address label printing and consignment tracking.',
    partners: [{ name: 'Shipmate', logo: '/integrations/shipmate.png', url: null }],
  },
  {
    id: 'others',
    title: 'Other Integrations',
    description:
      'Bespoke connectors to trade-specific tools our customers rely on.',
    partners: [{ name: 'Haybrooke PDQ', logo: '/integrations/haybrooke.webp', url: null }],
  },
  {
    id: 'api',
    title: 'Imprint API',
    description:
      "Not on the list? Our own RESTful API lets you connect Imprint MIS to virtually any system — CRMs, ERPs, custom in-house tools. If your team can code against a JSON endpoint, we can integrate.",
    partners: [],
    isApi: true,
  },
];

